/**
 * Kitchen wire types and calls. Field names are the backend's (snake_case), copied from
 * `services/core-api/src/modules/kitchen` and `docs/09_API_SPECIFICATION.md` section 14.3, which is
 * authoritative over the older example in section 5.
 *
 * Nothing financial exists in these types. The void response also carries `revised_total_amount`; it is
 * dropped here, at the edge, so an amount can never reach a kitchen screen (BR-CONF-005).
 */
import { staffApi } from '@/api/instances';
import type { ApiClient } from '@/api/client';

export interface KitchenItem {
  order_item_id: string;
  name: string;
  quantity: number;
  /** `SUBMITTED`, `IN_PREPARATION`, `READY` or `SERVED`. Cancelled lines are not returned. */
  fulfillment_status: string;
  modifiers: string[];
  notes: string | null;
}

export interface KitchenTicket {
  order_id: string;
  /** `ORD-0042`. */
  order_number: string;
  location_id: string;
  location_label: string;
  /** `TABLE` or `SEAT`. */
  location_type: string;
  zone_name: string | null;
  party_name: string;
  notes: string | null;
  created_at: string;
  /** Seconds since the order was submitted, as the server saw it when it answered. */
  elapsed_seconds: number;
  /** `SUBMITTED`, `IN_PREPARATION` or `READY`. */
  status: string;
  items: KitchenItem[];
}

export interface ItemStatusResult {
  order_item_id: string;
  fulfillment_status: string;
  order_status: string;
  updated_at: string;
}

export interface VoidItemInput {
  /** Required by the backend, up to 50 characters. The backend defines no list of codes. */
  reason_code: string;
  /** Optional, up to 200 characters. */
  notes?: string;
}

export interface VoidItemResult {
  order_item_id: string;
  fulfillment_status: string;
  order_status: string;
  voided_by: string;
  voided_at: string;
}

export interface ReprintResult {
  print_job_id: string;
  document_type: string;
  printer_id: string;
  is_reprint: boolean;
  reprint_indicator: string | null;
  reprint_counter: number;
  /** `PENDING`, `SENT` or `FAILED`. A printer failure is a 200 with `FAILED`. */
  status: string;
}

/** One dish on the kitchen's availability list (GET /kitchen/menu). No price is sent. */
export interface KitchenMenuItem {
  menu_item_id: string;
  name: string;
  is_available: boolean;
  is_special: boolean;
}

export interface KitchenMenuCategory {
  category_id: string;
  name: string;
  items: KitchenMenuItem[];
}

export interface AvailabilityResult {
  menu_item_id: string;
  is_available: boolean;
  updated_at: string;
}

export type AssistanceKind = 'CALL_WAITER' | 'WATER' | 'CUTLERY' | 'BILL' | 'OTHER';

export interface AssistanceRequest {
  request_id: string;
  type: string;
  note: string | null;
  status: string;
  created_at: string;
  resolved_at: string | null;
  session_id: string;
  party_display_name: string;
  location_id: string;
  location_label: string;
  location_type: string;
  zone_name: string | null;
}

export function createKitchenApi(client: ApiClient) {
  return {
    getTickets: (signal?: AbortSignal) =>
      client.get<KitchenTicket[]>('/kitchen/tickets', { signal }),

    /** The kitchen prepares; serving is the waiter's (the backend refuses SERVED from the kitchen). */
    updateItemStatus: (orderItemId: string, status: 'IN_PREPARATION' | 'READY') =>
      client.patch<ItemStatusResult>(`/kitchen/items/${encodeURIComponent(orderItemId)}/status`, {
        status,
      }),

    getMenu: (signal?: AbortSignal) =>
      client.get<KitchenMenuCategory[]>('/kitchen/menu', { signal }),

    /** Sets the branch sold-out flag. Repeating the current state returns it; nothing else changes. */
    setAvailability: (menuItemId: string, isAvailable: boolean) =>
      client.post<AvailabilityResult>(
        `/kitchen/items/${encodeURIComponent(menuItemId)}/availability`,
        { is_available: isAvailable },
      ),

    voidItem: async (orderItemId: string, input: VoidItemInput): Promise<VoidItemResult> => {
      const raw = await client.post<VoidItemResult & { revised_total_amount?: unknown }>(
        `/kitchen/items/${encodeURIComponent(orderItemId)}/void`,
        input,
      );
      return {
        order_item_id: raw.order_item_id,
        fulfillment_status: raw.fulfillment_status,
        order_status: raw.order_status,
        voided_by: raw.voided_by,
        voided_at: raw.voided_at,
      };
    },

    /** Always a reprint. One idempotency key per press: a repeat with the same key returns the same job. */
    reprintTicket: (orderId: string, idempotencyKey: string) =>
      client.post<ReprintResult>(
        `/kitchen/tickets/${encodeURIComponent(orderId)}/reprint`,
        {},
        { idempotencyKey },
      ),

    getAssistance: (signal?: AbortSignal) =>
      client.get<AssistanceRequest[]>('/staff/assistance', { signal }),

    resolveAssistance: (requestId: string) =>
      client.post<AssistanceRequest>(`/staff/assistance/${encodeURIComponent(requestId)}/resolve`, {
        status: 'COMPLETED',
      }),
  };
}

export const kitchenApi = createKitchenApi(staffApi);
